import { useState, useEffect, useContext, useRef } from "react";
import { useForm } from "react-hook-form";
import { useRouteContext } from "@tanstack/react-router";
import { ChildSearchForm, ChildSearchFormRef } from "./ChildSearchForm";
import WizardPopup from "@/components/shared/WizardPopup";
import {
  ChildSearchResults,
  ChildSearchResultsRef,
} from "./ChildSearchResults";
import { CHLDWizardForm } from "./forms/CHLDWizardForm";
import { FAMIWizardForm } from "./forms/FAMIWizardForm";
import { LANGWizardForm } from "./forms/LANGWizardForm";
import { SLNGWizardForm } from "./forms/SLNGWizardForm";
import { CLENWizardForm } from "./forms/CLENWizardForm";
import { ATTNWizardForm } from "./forms/ATTNWizardForm";
import { PRGMWizardForm } from "./forms/PRGMWizardForm";
import { StepIndicator } from "@/components/shared/StepIndicator";
import {
  useAddChildWizard,
  MAX_CLASSROOM_ENROLLMENTS,
  MAX_ATTENDANCE_RECORDS,
  MAX_PRGM_RECORDS,
  PRE_POPULATED_FIELDS,
} from "./hooks/useAddChildWizard";
import { useChildSubmission } from "./hooks/useChildSubmission";
import { SkipOptionalStep } from "@/components/shared/OptionalStepSkipCheckbox";
import { ENRLWizardForm } from "./forms/ENLRWizardForm";
import { ReviewSteps } from "./forms/ReviewSteps";
import { useLeaData } from "@/queries";
import { FileSubmissionsContext } from "@/components/layout/FileSubmissionsTracker";
import LoaderButton from "@/components/shared/LoaderButton";
import Spinner from "react-bootstrap/Spinner";
import { ComboBoxOption } from "@/components/shared/forms/SearchComboBox";
import { FaPlus } from "react-icons/fa";
import { type FAMI, FAMI_TEMPLATE } from "@/models/fami.tsx";
import { type ATTN, ATTN_TEMPLATE, type ENRL } from "@/models";
import { ConfirmModal } from "@/components/shared/ConfirmModal";
import { focusFirstInvalidField } from "@/utils/forms";

type PreschoolSummary = {
  PreschoolCDSCode: string;
  PreschoolName: string;
};

type AddChildWizardProps = {
  show: boolean;
  onClose: () => void;
  preschools: PreschoolSummary[];
};

// All possible wizard steps with their permission requirements
const ALL_WIZARD_STEPS = [
  { num: 1, label: "Search", alwaysShow: true },
  { num: 2, label: "Basic Information", alwaysShow: true },
  {
    num: 3,
    label: "Family",
    subtitle: "(Optional)",
    permissionKey: "FAMIEdit",
  },
  {
    num: 4,
    label: "Language",
    subtitle: "(Optional)",
    permissionKey: "LANGEdit",
  },
  {
    num: 5,
    label: "Supplemental Language",
    subtitle: "(Optional)",
    permissionKey: "SLNGEdit",
  },
  {
    num: 6,
    label: "Enrollment",
    subtitle: "(Optional)",
    permissionKey: "ENRLEdit",
  },
  {
    num: 7,
    label: "Classroom Enrollment",
    subtitle: "(Optional)",
    permissionKey: "CLENEdit",
  },
  {
    num: 8,
    label: "Attendance",
    subtitle: "(Optional)",
    permissionKey: "ATTNEdit",
  },
  {
    num: 9,
    label: "Education Programs",
    subtitle: "(Optional)",
    permissionKey: "PRGMEdit",
  },
  { num: 10, label: "Review & Submit", alwaysShow: true },
];

export function AddChildWizard({
  show,
  onClose,
  preschools,
}: AddChildWizardProps) {
  const {
    currentStep,
    completedSteps,
    searchResults,
    showResults,
    searchedData,
    isNewRecord,
    lockedFields,
    form,
    familyForm,
    languageForm,
    supplementalLanguageForm,
    enrollmentForm,
    classroomEnrollmentForms,
    activeClassroomCount,
    attendanceForms,
    activeAttendanceCount,
    attendanceMonthsInUse,
    prgmForms,
    activePrgmCount,
    canCreateNew,
    searchError,
    isSearching,
    searchCriteria,
    skipFamily,
    skipLanguage,
    skipSupplementalLanguage,
    skipEnrollment,
    skipClassroomEnrollment,
    skipAttendance,
    skipPrgm,
    hasReachedReview,
    handleSearch,
    handleSelectChild,
    handleCreateNew,
    resetWizard: originalResetWizard,
    setCurrentStep,
    setCompletedSteps,
    clearSearchError,
    handleSkipFamily,
    handleSkipLanguage,
    handleSkipSupplementalLanguage,
    handleSkipEnrollment,
    handleSkipClassroomEnrollment,
    handleSkipAttendance,
    handleSkipPrgm,
    addClassroomEnrollment,
    removeClassroomEnrollment,
    addAttendanceRecord,
    removeAttendanceRecord,
    addPrgmRecord,
    removePrgmRecord,
    skipToReview,
    prePopulateAllFormsFromCHLD,
  } = useAddChildWizard();

  const familyForm2 = useForm<FAMI>({ defaultValues: { ...FAMI_TEMPLATE } });
  const [showSecondFamily, setShowSecondFamily] = useState(false);

  const handleAddSecondFamily = () => {
    setShowSecondFamily(true);
    const chldValues = form.getValues();
    familyForm2.setValue("PreschoolCode", chldValues.PreschoolCode ?? "");
    familyForm2.setValue("ProgramYear", chldValues.ProgramYear ?? "");
    familyForm2.setValue(
      "EffectiveStartDate",
      chldValues.EffectiveStartDate ?? ""
    );
  };

  const handleRemoveSecondFamily = () => {
    familyForm2.reset();
    setShowSecondFamily(false);
  };

  const [removeConfirmation, setRemoveConfirmation] = useState<{
    label: string;
    onConfirm: () => void;
    fallbackFocusRef?: React.RefObject<HTMLElement | null>;
  } | null>(null);

  const requestRemoveConfirmation = (
    label: string,
    onConfirm: () => void,
    fallbackFocusRef?: React.RefObject<HTMLElement | null>
  ) => {
    setRemoveConfirmation({ label, onConfirm, fallbackFocusRef });
  };

  // Durable "Add Another..." targets to refocus after a confirmed removal
  // unmounts the button that triggered the confirmation.
  const addSecondFamilyRef = useRef<HTMLDivElement>(null);
  const addClassroomEnrollmentRef = useRef<HTMLDivElement>(null);
  const addAttendanceRecordRef = useRef<HTMLDivElement>(null);
  const addPrgmRecordRef = useRef<HTMLDivElement>(null);

  const stepHeadingRef = useRef<HTMLHeadingElement>(null);
  const isInitialStepRender = useRef(true);
  const wizardLoadingRef = useRef<HTMLDivElement>(null);

  const handleCancelRemove = () => setRemoveConfirmation(null);

  const handleConfirmRemove = () => {
    removeConfirmation?.onConfirm();
    setRemoveConfirmation(null);
  };

  const resetWizard = () => {
    originalResetWizard();
    familyForm2.reset();
    setShowSecondFamily(false);
  };

  const leaContext = useRouteContext({ from: "/$leaid" });
  const coreData = leaContext.coreData;
  const esiIdPath = leaContext.currentUserOrg.esiIdPath;
  const result = useLeaData(esiIdPath);
  const leaData = result.data;

  const canEditFamily = leaContext.hasRole("FAMIEdit");
  const canEditLanguage = leaContext.hasRole("LANGEdit");
  const canEditSupplementalLanguage = leaContext.hasRole("SLNGEdit");
  const canEditEnrollment = leaContext.hasRole("ENRLEdit");
  const canEditClassroomEnrollment = leaContext.hasRole("CLENEdit");
  const canEditAttendance =
    leaContext.hasRole("ATTNEdit") && leaContext.hasFeature("Attendance");
  const canEditPrgm =
    leaContext.hasRole("PRGMEdit") &&
    leaContext.hasFeature("Education_Programs");

  const permissionMap: Record<string, boolean> = {
    FAMIEdit: !!canEditFamily,
    LANGEdit: !!canEditLanguage,
    SLNGEdit: !!canEditSupplementalLanguage,
    ENRLEdit: !!canEditEnrollment,
    CLENEdit: !!canEditClassroomEnrollment,
    ATTNEdit: !!canEditAttendance,
    PRGMEdit: !!canEditPrgm,
  };

  const availableSteps = ALL_WIZARD_STEPS.filter(
    (step) => step.alwaysShow || !!permissionMap[step.permissionKey ?? ""]
  ).map((step, index) => ({
    ...step,
    displayNum: index + 1,
  }));

  const availableStepNums = availableSteps.map((s) => s.num);

  const getNextAvailableStep = (current: number): number => {
    const currentIndex = availableStepNums.indexOf(current);
    if (currentIndex < availableStepNums.length - 1) {
      return availableStepNums[currentIndex + 1];
    }
    return current;
  };

  const getPrevAvailableStep = (current: number): number => {
    const currentIndex = availableStepNums.indexOf(current);
    if (currentIndex > 0) {
      return availableStepNums[currentIndex - 1];
    }
    return current;
  };

  const isStepActionable = (stepNum: number) =>
    stepNum <= currentStep || completedSteps.includes(stepNum);

  const validateActiveClassroomForms = async () => {
    const results = await Promise.all(
      classroomEnrollmentForms
        .slice(0, activeClassroomCount)
        .map((f) => f.trigger())
    );
    return results.every(Boolean);
  };

  const validateActiveAttendanceForms = async () => {
    const results = await Promise.all(
      attendanceForms.slice(0, activeAttendanceCount).map((f) => f.trigger())
    );
    return results.every(Boolean);
  };

  const validateActivePrgmForms = async () => {
    const results = await Promise.all(
      prgmForms.slice(0, activePrgmCount).map((f) => f.trigger())
    );
    return results.every(Boolean);
  };

  const handleNext = async () => {
    let isValid = true;

    if (currentStep === 2) {
      isValid = await form.trigger();
      if (isValid) {
        prePopulateAllFormsFromCHLD();
      }
    } else if (currentStep === 3 && !skipFamily) {
      isValid = await familyForm.trigger();
      if (isValid && showSecondFamily) {
        isValid = await familyForm2.trigger();
      }
    } else if (currentStep === 4 && !skipLanguage) {
      isValid = await languageForm.trigger();
    } else if (currentStep === 5 && !skipSupplementalLanguage) {
      isValid = await supplementalLanguageForm.trigger();
    } else if (currentStep === 6 && !skipEnrollment) {
      isValid = await enrollmentForm.trigger();
    } else if (currentStep === 7 && !skipClassroomEnrollment) {
      isValid = await validateActiveClassroomForms();
    } else if (currentStep === 8 && !skipAttendance) {
      isValid = await validateActiveAttendanceForms();
    } else if (currentStep === 9 && !skipPrgm) {
      isValid = await validateActivePrgmForms();
    }

    if (isValid) {
      const nextStep = getNextAvailableStep(currentStep);
      setCurrentStep(nextStep);
      if (!completedSteps.includes(currentStep)) {
        setCompletedSteps((prev) =>
          [...prev, currentStep].sort((a, b) => a - b)
        );
      }
    } else {
      focusFirstInvalidField(".add-child-wizard .modal-body");
    }
  };

  const [showBackToSearchConfirm, setShowBackToSearchConfirm] = useState(false);

  const autoSkipCurrentStepIfEmpty = () => {
    const formHasUserData = (values: Record<string, any>): boolean => {
      return Object.keys(values).some((key) => {
        if (PRE_POPULATED_FIELDS.has(key)) return false;
        const val = values[key];
        return val !== "" && val !== undefined && val !== null;
      });
    };

    if (currentStep === 3 && !skipFamily) {
      const family1HasData = formHasUserData(
        familyForm.getValues() as Record<string, any>
      );
      const family2HasData =
        showSecondFamily &&
        formHasUserData(familyForm2.getValues() as Record<string, any>);
      if (!family1HasData && !family2HasData) {
        if (showSecondFamily) {
          handleRemoveSecondFamily();
        }
        handleSkipFamily(true);
      }
    } else if (currentStep === 4 && !skipLanguage) {
      if (!formHasUserData(languageForm.getValues() as Record<string, any>)) {
        handleSkipLanguage(true);
      }
    } else if (currentStep === 5 && !skipSupplementalLanguage) {
      if (
        !formHasUserData(
          supplementalLanguageForm.getValues() as Record<string, any>
        )
      ) {
        handleSkipSupplementalLanguage(true);
      }
    } else if (currentStep === 6 && !skipEnrollment) {
      if (!formHasUserData(enrollmentForm.getValues() as Record<string, any>)) {
        handleSkipEnrollment(true);
      }
    } else if (currentStep === 7 && !skipClassroomEnrollment) {
      const anyClassroomHasData = classroomEnrollmentForms
        .slice(0, activeClassroomCount)
        .some((f) => formHasUserData(f.getValues() as Record<string, any>));
      if (!anyClassroomHasData) {
        handleSkipClassroomEnrollment(true);
      }
    } else if (currentStep === 8 && !skipAttendance) {
      const anyAttendanceHasData = attendanceForms
        .slice(0, activeAttendanceCount)
        .some((f) => formHasUserData(f.getValues() as Record<string, any>));
      if (!anyAttendanceHasData) {
        handleSkipAttendance(true);
      }
    } else if (currentStep === 9 && !skipPrgm) {
      const anyPrgmHasData = prgmForms
        .slice(0, activePrgmCount)
        .some((f) => formHasUserData(f.getValues() as Record<string, any>));
      if (!anyPrgmHasData) {
        handleSkipPrgm(true);
      }
    }
  };

  const handleBack = () => {
    if (currentStep === 2) {
      setShowBackToSearchConfirm(true);
      return;
    }
    if (hasReachedReview) {
      autoSkipCurrentStepIfEmpty();
    }
    const prevStep = getPrevAvailableStep(currentStep);
    setCurrentStep(prevStep);
  };

  const handleConfirmBackToSearch = () => {
    setShowBackToSearchConfirm(false);
    resetWizard();
  };

  const handleCancelBackToSearch = () => {
    setShowBackToSearchConfirm(false);
  };

  useEffect(() => {
    if (!canEditFamily) {
      handleSkipFamily(true);
    }
    if (!canEditLanguage) {
      handleSkipLanguage(true);
    }
    if (!canEditSupplementalLanguage) {
      handleSkipSupplementalLanguage(true);
    }
    if (!canEditEnrollment) {
      handleSkipEnrollment(true);
    }
    if (!canEditClassroomEnrollment) {
      handleSkipClassroomEnrollment(true);
    }
    if (!canEditAttendance) {
      handleSkipAttendance(true);
    }
    if (!canEditPrgm) {
      handleSkipPrgm(true);
    }
  }, [
    canEditFamily,
    canEditLanguage,
    canEditSupplementalLanguage,
    canEditEnrollment,
    canEditClassroomEnrollment,
    canEditAttendance,
    canEditPrgm,
  ]);

  const { addPendingFileSubmission } = useContext(FileSubmissionsContext);

  const [shouldClose, setShouldClose] = useState(false);

  const [chldError, setChldError] = useState<string | null>(null);

  const searchFormRef = useRef<ChildSearchFormRef>(null);

  const searchResultsRef = useRef<ChildSearchResultsRef>(null);

  const handleChldPosted = () => {
    setChldError(null);
    setShouldClose(true);
  };

  useEffect(() => {
    if (shouldClose) {
      setShouldClose(false);
      onClose();
      resetWizard();
    }
  }, [shouldClose, onClose]);

  const handleChldFailed = (errors: any[]) => {
    setCurrentStep(2);

    const errorMessage =
      errors.length > 0
        ? errors[0]?.Message ||
          errors[0]?.message ||
          "Validation error occurred."
        : "CHLD validation failed. Please check your data and try again.";

    setChldError(errorMessage);
  };

  useEffect(() => {
    if (currentStep !== 2 && chldError) {
      setChldError(null);
    }
  }, [currentStep, chldError]);

  const { submitChild, isSubmitting, phase } = useChildSubmission(
    {
      chldForm: form,
      familyForm,
      familyForm2,
      languageForm,
      supplementalLanguageForm,
      enrollmentForm,
      classroomEnrollmentForms,
      attendanceForms,
      prgmForms,
    },
    {
      skipFamily,
      skipLanguage,
      skipSupplementalLanguage,
      skipEnrollment,
      skipClassroomEnrollment,
      showSecondFamily,
      activeClassroomCount,
      skipAttendance,
      activeAttendanceCount,
      skipPrgm,
      activePrgmCount,
    },
    leaData,
    esiIdPath,
    {
      onChldPosted: handleChldPosted,
      onChldFailed: handleChldFailed,
      addPendingFileSubmission,
    }
  );

  const watchPreschoolCode = form.watch("PreschoolCode");
  const watchProgramYear = form.watch("ProgramYear");

  const classroomOptions: ComboBoxOption[] = leaData?.entities?.classrooms
    ? Object.values(leaData.entities.classrooms)
        .map((c) => c.records[0])
        .filter(
          (clrm) =>
            clrm &&
            clrm.LocalClassroomId &&
            clrm.PreschoolCode === watchPreschoolCode &&
            clrm.ProgramYear === watchProgramYear &&
            !clrm.EffectiveEndDate
        )
        .map((clrm) => ({
          value: clrm.LocalClassroomId,
          label: clrm.ClassroomName ?? "",
          sublabel: `ID: ${clrm.LocalClassroomId}`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
    : [];

  const attendanceEnrlRecords: ENRL[] = skipEnrollment
    ? []
    : [enrollmentForm.getValues()];

  const getSiblingAttnRecords = (index: number): ATTN[] =>
    attendanceMonthsInUse
      .map((month, i) => ({ index: i, month }))
      .filter(
        ({ index: i, month }) =>
          i !== index && i < activeAttendanceCount && !!month
      )
      .map(({ month }) => ({
        ...ATTN_TEMPLATE,
        ProgramYear: watchProgramYear,
        PreschoolCode: watchPreschoolCode,
        FirstOfMonth: month,
      }));

  // Show loading spinner while CHLD is being submitted, waiting to be processed, or submitting related records
  const showWizardLoading =
    isSubmitting &&
    (phase === "submitting-chld" ||
      phase === "waiting-for-chld" ||
      phase === "submitting-related");

  useEffect(() => {
    if (showWizardLoading) {
      wizardLoadingRef.current?.focus();
    }
  }, [showWizardLoading]);

  const [showExitConfirm, setShowExitConfirm] = useState(false);

  // Scroll to top when step changes
  useEffect(() => {
    const modalBody = document.querySelector(".add-child-wizard .modal-body");
    if (modalBody) {
      modalBody.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [currentStep, showResults]);

  useEffect(() => {
    if (isInitialStepRender.current) {
      isInitialStepRender.current = false;
      return;
    }
    stepHeadingRef.current?.focus();
  }, [currentStep, showResults]);

  const handleCloseAttempt = () => {
    if (currentStep >= 2) {
      setShowExitConfirm(true);
    } else {
      handleCancel();
    }
  };

  const handleConfirmExit = () => {
    setShowExitConfirm(false);
    handleCancel();
  };

  const handleCancelExit = () => {
    setShowExitConfirm(false);
  };

  const handleCancel = () => {
    resetWizard();
    onClose();
  };

  const handleBackToSearch = () => {
    resetWizard();
  };

  const handleStepClick = async (stepNum: number) => {
    // Don't allow clicking from step 1 to step 2 directly
    if (currentStep === 1 && stepNum === 2) {
      return;
    }

    if (!availableStepNums.includes(stepNum)) {
      return;
    }

    // Clicking Search step from step 2+ should warn about data loss
    if (stepNum === 1 && currentStep >= 2) {
      setShowBackToSearchConfirm(true);
      return;
    }

    const canNavigateBack = stepNum <= currentStep;
    const canNavigateForward =
      stepNum > currentStep && completedSteps.includes(stepNum);

    if (canNavigateBack) {
      if (hasReachedReview) {
        autoSkipCurrentStepIfEmpty();
      }
      setCurrentStep(stepNum);
      return;
    }

    if (canNavigateForward) {
      let isValid = true;

      if (currentStep === 2) {
        isValid = await form.trigger();
      } else if (currentStep === 3 && !skipFamily) {
        isValid = await familyForm.trigger();
        if (isValid && showSecondFamily) {
          isValid = await familyForm2.trigger();
        }
      } else if (currentStep === 4 && !skipLanguage) {
        isValid = await languageForm.trigger();
      } else if (currentStep === 5 && !skipSupplementalLanguage) {
        isValid = await supplementalLanguageForm.trigger();
      } else if (currentStep === 6 && !skipEnrollment) {
        isValid = await enrollmentForm.trigger();
      } else if (currentStep === 7 && !skipClassroomEnrollment) {
        isValid = await validateActiveClassroomForms();
      } else if (currentStep === 8 && !skipAttendance) {
        isValid = await validateActiveAttendanceForms();
      } else if (currentStep === 9 && !skipPrgm) {
        isValid = await validateActivePrgmForms();
      }

      if (isValid) {
        setCurrentStep(stepNum);
      } else {
        focusFirstInvalidField(".add-child-wizard .modal-body");
      }
    }
  };

  const handleSubmit = () => {
    // Clear any previous errors
    setChldError(null);
    submitChild(isNewRecord);
  };

  const isLastStep =
    currentStep === availableStepNums[availableStepNums.length - 1];

  const showSkipToReview = currentStep >= 3 && currentStep <= 9;

  const renderFooter = () => {
    // For step 1 (search), render the fixed footer with appropriate buttons
    if (currentStep === 1) {
      // If showing search results, show Previous button only
      if (showResults) {
        return (
          <div className="d-flex justify-content-end gap-2 w-100">
            <button
              className="btn btn-outline-secondary"
              onClick={() => searchResultsRef.current?.handleBack()}
              disabled={isSubmitting}
            >
              Previous
            </button>
          </div>
        );
      }

      // If showing search form, show Cancel and Search buttons
      return (
        <div className="d-flex justify-content-end gap-2 w-100">
          <button
            className="btn btn-outline-secondary"
            onClick={() => searchFormRef.current?.handleCancel()}
            disabled={isSubmitting || isSearching}
          >
            Cancel
          </button>
          <LoaderButton
            variant="primary"
            className="wizard-btn-primary"
            onClick={() => searchFormRef.current?.handleSearch()}
            loading={isSearching}
            loadingText="Searching..."
            disabled={isSubmitting || isSearching}
          >
            Search
          </LoaderButton>
        </div>
      );
    }

    return (
      <div className="d-flex align-items-center w-100">
        <div>
          <button
            className="btn btn-outline-secondary"
            onClick={handleBack}
            disabled={isSubmitting}
          >
            Previous
          </button>
        </div>
        <div className="ms-auto d-flex gap-2">
          {showSkipToReview && (
            <button
              className="btn btn-outline-primary wizard-btn-outline-primary"
              onClick={skipToReview}
              disabled={isSubmitting}
            >
              Skip to Review
            </button>
          )}
          {isLastStep ? (
            <LoaderButton
              variant="primary"
              className="wizard-btn-primary"
              onClick={handleSubmit}
              loading={isSubmitting && !showWizardLoading}
              loadingText="Submitting..."
              disabled={isSubmitting}
            >
              Submit
            </LoaderButton>
          ) : (
            <button
              className="btn btn-primary wizard-btn-primary"
              onClick={handleNext}
              disabled={isSubmitting}
            >
              Next
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <WizardPopup
        show={show}
        header={
          <>
            <i className="bi bi-person-plus"></i>Add Child
          </>
        }
        size="xl"
        onClose={handleCloseAttempt}
        enforceFocus={
          !showExitConfirm && !showBackToSearchConfirm && !removeConfirmation
        }
        hasButton={false}
        className="add-child-wizard"
        backdrop="static"
        footer={renderFooter()}
      >
        {showWizardLoading ? (
          <div
            className="wizard-loading-container"
            role="alert"
            tabIndex={-1}
            ref={wizardLoadingRef}
          >
            <Spinner
              animation="border"
              variant="primary"
              className="wizard-loading-spinner"
            />
            <p className="wizard-loading-title">
              Waiting for CHLD record to be processed...
            </p>
            <p className="wizard-loading-subtitle">
              Please do not close this window.
            </p>
          </div>
        ) : (
          <div className="wizard-content">
            <div>
              <StepIndicator
                steps={availableSteps}
                currentStep={currentStep}
                completedSteps={completedSteps}
                onStepClick={handleStepClick}
                isStepActionable={isStepActionable}
                skippedSteps={[
                  skipFamily && canEditFamily ? 3 : null,
                  skipLanguage && canEditLanguage ? 4 : null,
                  skipSupplementalLanguage && canEditSupplementalLanguage
                    ? 5
                    : null,
                  skipEnrollment && canEditEnrollment ? 6 : null,
                  skipClassroomEnrollment && canEditClassroomEnrollment
                    ? 7
                    : null,
                  skipAttendance && canEditAttendance ? 8 : null,
                  skipPrgm && canEditPrgm ? 9 : null,
                ].filter((s): s is number => s !== null)}
              />
            </div>

            <hr className="wizard-divider" />

            <h2 ref={stepHeadingRef} tabIndex={-1} className="visually-hidden">
              {`Step ${
                availableSteps.find((s) => s.num === currentStep)?.displayNum ??
                currentStep
              }: ${
                availableSteps.find((s) => s.num === currentStep)?.label ?? ""
              }`}
            </h2>

            {currentStep === 1 && !showResults && (
              <ChildSearchForm
                ref={searchFormRef}
                preschools={preschools}
                disabled={isSearching}
                onSearch={handleSearch}
                onCancel={handleCancel}
                searchError={searchError}
                onClearSearchError={clearSearchError}
                searchCriteria={searchCriteria}
              />
            )}

            {currentStep === 1 && showResults && (
              <ChildSearchResults
                ref={searchResultsRef}
                searchResults={searchResults}
                searchedName={searchedData.name}
                searchedDOB={searchedData.dob}
                onSelectChild={handleSelectChild}
                onCreateNew={handleCreateNew}
                onBack={handleBackToSearch}
                canCreateNew={canCreateNew}
                preschools={preschools}
              />
            )}

            {currentStep === 2 && (
              <>
                {chldError && (
                  <div className="wizard-error-banner">
                    <strong>CHLD Validation Failed:</strong> {chldError}
                  </div>
                )}
                <CHLDWizardForm
                  form={form}
                  isNewRecord={isNewRecord}
                  lockedFields={lockedFields}
                />
              </>
            )}

            {currentStep === 3 && canEditFamily && (
              <SkipOptionalStep
                checked={skipFamily}
                onChange={(checked) => {
                  handleSkipFamily(checked);
                  if (checked && showSecondFamily) {
                    handleRemoveSecondFamily();
                  }
                }}
              >
                <FAMIWizardForm
                  form={familyForm}
                  preschools={preschools}
                  familyNumber={1}
                  footer={
                    showSecondFamily ? (
                      <FAMIWizardForm
                        form={familyForm2}
                        preschools={preschools}
                        familyNumber={2}
                        onRemove={() =>
                          requestRemoveConfirmation(
                            "Family 2 Information",
                            handleRemoveSecondFamily,
                            addSecondFamilyRef
                          )
                        }
                      />
                    ) : (
                      <div
                        ref={addSecondFamilyRef}
                        className="wizard-add-family-btn"
                        role="button"
                        tabIndex={0}
                        onClick={handleAddSecondFamily}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleAddSecondFamily();
                          }
                        }}
                      >
                        <FaPlus className="me-2 wizard-add-family-icon" />
                        <span className="wizard-add-family-text">
                          Add a Second Family Record (Optional)
                        </span>
                      </div>
                    )
                  }
                />
              </SkipOptionalStep>
            )}

            {currentStep === 4 && canEditLanguage && (
              <SkipOptionalStep
                checked={skipLanguage}
                onChange={handleSkipLanguage}
              >
                <LANGWizardForm form={languageForm} preschools={preschools} />
              </SkipOptionalStep>
            )}

            {currentStep === 5 && canEditSupplementalLanguage && (
              <SkipOptionalStep
                checked={skipSupplementalLanguage}
                onChange={handleSkipSupplementalLanguage}
              >
                <SLNGWizardForm
                  form={supplementalLanguageForm}
                  preschools={preschools}
                />
              </SkipOptionalStep>
            )}

            {currentStep === 6 && canEditEnrollment && (
              <SkipOptionalStep
                checked={skipEnrollment}
                onChange={handleSkipEnrollment}
              >
                <ENRLWizardForm form={enrollmentForm} preschools={preschools} />
              </SkipOptionalStep>
            )}

            {currentStep === 7 && canEditClassroomEnrollment && (
              <SkipOptionalStep
                checked={skipClassroomEnrollment}
                onChange={handleSkipClassroomEnrollment}
              >
                <>
                  {classroomEnrollmentForms
                    .slice(0, activeClassroomCount)
                    .map((f, i) => (
                      <CLENWizardForm
                        key={i}
                        form={f}
                        preschools={preschools}
                        classroomOptions={classroomOptions}
                        classroomNumber={i + 1}
                        onRemove={
                          i === 0
                            ? undefined
                            : () =>
                                requestRemoveConfirmation(
                                  `Classroom Enrollment ${i + 1}`,
                                  () => removeClassroomEnrollment(i),
                                  addClassroomEnrollmentRef
                                )
                        }
                      />
                    ))}

                  {activeClassroomCount < MAX_CLASSROOM_ENROLLMENTS && (
                    <div className="ps-3 pe-3">
                      <div
                        ref={addClassroomEnrollmentRef}
                        className="wizard-add-family-btn"
                        role="button"
                        tabIndex={0}
                        onClick={addClassroomEnrollment}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            addClassroomEnrollment();
                          }
                        }}
                      >
                        <FaPlus className="me-2 wizard-add-family-icon" />
                        <span className="wizard-add-family-text">
                          Add Another Classroom Enrollment (Optional)
                        </span>
                      </div>
                    </div>
                  )}
                </>
              </SkipOptionalStep>
            )}

            {currentStep === 8 && canEditAttendance && (
              <SkipOptionalStep
                checked={skipAttendance}
                onChange={handleSkipAttendance}
              >
                <>
                  {attendanceForms
                    .slice(0, activeAttendanceCount)
                    .map((f, i) => (
                      <ATTNWizardForm
                        key={i}
                        form={f}
                        preschools={preschools}
                        enrlRecords={attendanceEnrlRecords}
                        siblingAttnRecords={getSiblingAttnRecords(i)}
                        attendanceNumber={i + 1}
                        onRemove={
                          i === 0
                            ? undefined
                            : () =>
                                requestRemoveConfirmation(
                                  `Attendance Record ${i + 1}`,
                                  () => removeAttendanceRecord(i),
                                  addAttendanceRecordRef
                                )
                        }
                      />
                    ))}

                  {activeAttendanceCount < MAX_ATTENDANCE_RECORDS && (
                    <div className="ps-3 pe-3">
                      <div
                        ref={addAttendanceRecordRef}
                        className="wizard-add-family-btn"
                        role="button"
                        tabIndex={0}
                        onClick={addAttendanceRecord}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            addAttendanceRecord();
                          }
                        }}
                      >
                        <FaPlus className="me-2 wizard-add-family-icon" />
                        <span className="wizard-add-family-text">
                          Add Another Attendance Record (Optional)
                        </span>
                      </div>
                    </div>
                  )}
                </>
              </SkipOptionalStep>
            )}

            {currentStep === 9 && canEditPrgm && (
              <SkipOptionalStep checked={skipPrgm} onChange={handleSkipPrgm}>
                <>
                  {prgmForms.slice(0, activePrgmCount).map((f, i) => (
                    <PRGMWizardForm
                      key={i}
                      form={f}
                      preschools={preschools}
                      prgmNumber={i + 1}
                      onRemove={
                        i === 0
                          ? undefined
                          : () =>
                              requestRemoveConfirmation(
                                `Education Program ${i + 1}`,
                                () => removePrgmRecord(i),
                                addPrgmRecordRef
                              )
                      }
                    />
                  ))}

                  {activePrgmCount < MAX_PRGM_RECORDS && (
                    <div className="ps-3 pe-3">
                      <div
                        ref={addPrgmRecordRef}
                        className="wizard-add-family-btn"
                        role="button"
                        tabIndex={0}
                        onClick={addPrgmRecord}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            addPrgmRecord();
                          }
                        }}
                      >
                        <FaPlus className="me-2 wizard-add-family-icon" />
                        <span className="wizard-add-family-text">
                          Add Another Education Program Record (Optional)
                        </span>
                      </div>
                    </div>
                  )}
                </>
              </SkipOptionalStep>
            )}

            {currentStep === 10 && (
              <ReviewSteps
                form={form}
                familyForm={familyForm}
                familyForm2={familyForm2}
                showSecondFamily={showSecondFamily}
                languageForm={languageForm}
                supplementalLanguageForm={supplementalLanguageForm}
                enrollmentForm={enrollmentForm}
                classroomEnrollmentForms={classroomEnrollmentForms}
                activeClassroomCount={activeClassroomCount}
                attendanceForms={attendanceForms}
                activeAttendanceCount={activeAttendanceCount}
                prgmForms={prgmForms}
                activePrgmCount={activePrgmCount}
                preschools={preschools}
                coreData={coreData}
                skipFamily={skipFamily}
                skipLanguage={skipLanguage}
                skipSupplementalLanguage={skipSupplementalLanguage}
                skipEnrollment={skipEnrollment}
                skipClassroomEnrollment={skipClassroomEnrollment}
                skipAttendance={skipAttendance}
                skipPrgm={skipPrgm}
                onEditStep={setCurrentStep}
                canEditFamily={canEditFamily}
                canEditLanguage={canEditLanguage}
                canEditSupplementalLanguage={canEditSupplementalLanguage}
                canEditEnrollment={canEditEnrollment}
                canEditClassroomEnrollment={canEditClassroomEnrollment}
                canEditAttendance={canEditAttendance}
                canEditPrgm={canEditPrgm}
              />
            )}
          </div>
        )}
      </WizardPopup>

      {showExitConfirm && (
        <ConfirmModal
          title="Are you sure you want to exit?"
          message="All unsaved data will be lost. This action cannot be undone."
          confirmText="Exit"
          onCancel={handleCancelExit}
          onConfirm={handleConfirmExit}
        />
      )}

      {showBackToSearchConfirm && (
        <ConfirmModal
          title="Return to Search?"
          message="Going back to the search screen will clear all entered data across all steps. This action cannot be undone."
          confirmText="Return to Search"
          onCancel={handleCancelBackToSearch}
          onConfirm={handleConfirmBackToSearch}
        />
      )}

      {removeConfirmation && (
        <ConfirmModal
          title={`Remove ${removeConfirmation.label}?`}
          message={`Are you sure you want to remove this ${removeConfirmation.label.toLowerCase()}? Any information entered will be lost.`}
          confirmText="Remove"
          cancelText="Cancel"
          onCancel={handleCancelRemove}
          onConfirm={handleConfirmRemove}
          fallbackFocusRef={removeConfirmation.fallbackFocusRef}
        />
      )}
    </>
  );
}
